import type { ReactNode } from 'react';
import { Wallet, Banknote, PiggyBank, TrendingUp, Building2, AlertTriangle, ChevronDown, Info, Receipt, FileText, FileCheck2, Percent, CalendarDays, Flame } from 'lucide-react';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useDashboard } from '@/hooks/useDashboard';
import { KpiCard } from '@/components/KpiCard';
import { Card, CardContent } from '@/components/ui/card';
import { Progress } from '@/components/ui/progress';
import { Skeleton } from '@/components/ui/skeleton';
import { Badge } from '@/components/ui/badge';
import { TierMarker } from '@/components/TierMarker';
import { fmtEuro, fmtDate } from '@/lib/utils';
import { useIsTV } from '@/hooks/useDevice';

/** Intitulé de groupe : repère visuel discret pour scanner la page d'un coup d'œil. */
function RowLabel({ icon, children }: { icon: ReactNode; children: ReactNode }) {
  return (
    <div className="mb-1.5 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">
      {icon}
      {children}
    </div>
  );
}

export function Overview() {
  const { data, isLoading, error } = useDashboard();
  const isTV = useIsTV();

  if (isLoading) return <OverviewSkeleton />;
  if (error || !data) return <ErrorPanel msg={(error as Error)?.message ?? 'Aucune donnée'} />;

  const { treasury, mrr, revenue, salary, charges, pipeline, inflows, targets } = data;

  // Tailles : la trésorerie porte les chiffres « titres », le reste reste dense.
  const headSize = isTV ? 'xl' : 'lg';
  const bodySize = isTV ? 'md' : 'sm';

  function SectionHint({ children }: { children: ReactNode }) {
    return (
      <Tooltip>
        <TooltipTrigger className="ml-1 inline-flex h-4 w-4 items-center justify-center rounded-full border border-border text-muted-foreground hover:text-foreground">
          <Info className="h-3 w-3" />
        </TooltipTrigger>
        <TooltipContent className="max-w-sm text-xs leading-relaxed">{children}</TooltipContent>
      </Tooltip>
    );
  }

  const savings = treasury.savings;

  return (
    <div className="space-y-3">
      {/* ─── 1. Trésorerie : ce que j'ai ─────────────────────────── */}
      <section>
        <RowLabel icon={<Wallet className="h-3 w-3" />}>Trésorerie</RowLabel>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiCard
            icon={<Wallet className="h-3.5 w-3.5" />}
            label="Compte opérationnel"
            value={fmtEuro(treasury.operating?.balance ?? 0)}
            sub={treasury.operating?.alert ? `⚠ Sous le plancher ${fmtEuro(treasury.operating?.floor ?? 0)}` : `Plancher ${fmtEuro(treasury.operating?.floor ?? 0)}`}
            tone={treasury.operating?.alert ? 'danger' : 'default'}
            size={headSize}
            hint="Solde du compte AKAZ COMMUNICATION (principal Qonto). Le plancher correspond à ~1,5 mois de charges."
          />
          <KpiCard
            icon={<PiggyBank className="h-3.5 w-3.5" />}
            label="Épargne"
            value={fmtEuro(savings?.balance ?? 0)}
            sub={savings ? `Palier ${savings.tierReached}/3${savings.nextTier !== null ? ` · vers P${savings.nextTier} ${Math.round(savings.progressToNext)}%` : ''}` : '—'}
            tone="info"
            size={headSize}
            progress={savings?.nextTier !== null ? savings?.progressToNext : undefined}
            progressClassName="bg-info"
            hint="Compte épargne. Objectifs : P1 = 3 mois de charges, P2 = 6 mois, P3 = 12 mois. Détail des paliers en bas de page."
          />
          <KpiCard
            icon={<Building2 className="h-3.5 w-3.5" />}
            label="TVA / impôts"
            value={fmtEuro(treasury.taxes?.balance ?? 0)}
            sub="Provisionné"
            tone="muted"
            size={headSize}
            hint="Compte dédié au provisionnement TVA + IS pour ne pas piocher dans l'opérationnel."
          />
          <KpiCard
            icon={<Banknote className="h-3.5 w-3.5" />}
            label="Total Qonto"
            value={fmtEuro(treasury.total)}
            sub={`${treasury.accounts.length} comptes`}
            size={headSize}
            hint="Somme des soldes de tous les comptes Qonto."
          />
        </div>
      </section>

      {/* ─── 2. Activité : ce que je génère vs mes objectifs ─────── */}
      <section>
        <RowLabel icon={<TrendingUp className="h-3 w-3" />}>Activité &amp; objectifs</RowLabel>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiCard
            size={bodySize}
            icon={<CalendarDays className="h-3.5 w-3.5" />}
            label="CA mois en cours"
            value={fmtEuro(revenue.caCurrentMonth)}
            sub="HT"
            hint="Chiffre d'affaires encaissé depuis le 1er du mois en cours. HT = hors taxes (montant avant TVA)."
          />
          <KpiCard
            size={bodySize}
            icon={<TrendingUp className="h-3.5 w-3.5" />}
            label="CA rolling 12m"
            value={fmtEuro(revenue.caRolling12)}
            sub={`Cible ${fmtEuro(revenue.target ?? 0)}/mois · ${revenue.progressPct}%`}
            tone={revenue.progressPct >= 100 ? 'success' : 'default'}
            progress={revenue.progressPct}
            progressClassName={revenue.progressPct >= 100 ? 'bg-success' : 'bg-primary'}
            hint="Rolling 12 months = moyenne mensuelle du chiffre d'affaires sur les 12 derniers mois glissants. Plus stable qu'un mois isolé, montre la tendance de fond."
          />
          <KpiCard
            size={bodySize}
            icon={<Receipt className="h-3.5 w-3.5" />}
            label="MRR identifié"
            value={fmtEuro(mrr.identified)}
            sub={`Objectif ${fmtEuro(targets.mrr_target ?? 0)} · ${mrr.progressPct}%`}
            tone="success"
            progress={mrr.progressPct}
            progressClassName="bg-success"
            hint="MRR (Monthly Recurring Revenue) = revenu récurrent mensuel : sites en maintenance + abos récurrents + coaching Stripe encaissé (30j glissants). Composition détaillée en bas de page."
          />
          <KpiCard
            size={bodySize}
            icon={<Flame className="h-3.5 w-3.5" />}
            label="Burn rate /mois"
            value={fmtEuro(data.burnRate)}
            sub="Charges + salaire"
            tone="warning"
            hint="Burn rate = rythme de dépenses mensuel moyen. Calcul : (charges + ton salaire SASU) ÷ nombre de mois avec activité, sur les 12 derniers mois."
          />
        </div>
      </section>

      {/* ─── 3. À récupérer / à surveiller ───────────────────────── */}
      <section>
        <RowLabel icon={<AlertTriangle className="h-3 w-3" />}>À récupérer &amp; surveiller</RowLabel>
        <div className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          <KpiCard
            size={bodySize}
            icon={<FileText className="h-3.5 w-3.5" />}
            label="Factures impayées"
            value={pipeline.unpaidInvoices.length}
            sub={fmtEuro(pipeline.totalUnpaid)}
            tone="danger"
            hint="Factures émises mais non encaissées. À relancer dès 30 jours d'impayé. C'est de l'argent dû mais pas dans ta trésorerie."
          />
          <KpiCard
            size={bodySize}
            icon={<FileText className="h-3.5 w-3.5" />}
            label="Devis en attente"
            value={pipeline.pendingQuotes.length}
            sub={fmtEuro(pipeline.totalPendingQuotes)}
            tone="warning"
            hint="Devis envoyés au client mais pas encore signés. Tant qu'ils ne sont pas signés, l'argent n'est pas garanti. À relancer."
          />
          <KpiCard
            size={bodySize}
            icon={<FileCheck2 className="h-3.5 w-3.5" />}
            label="Devis signés"
            value={pipeline.approvedQuotes.length}
            sub={fmtEuro(pipeline.totalApprovedQuotes)}
            tone="info"
            hint="Devis approuvés et signés, mais pas encore convertis en facture. L'argent ne rentre que quand la facture est émise et encaissée."
          />
          <KpiCard
            size={bodySize}
            icon={<Percent className="h-3.5 w-3.5" />}
            label="Ratio charges"
            value={`${charges.ratioPct}%`}
            sub={`Cible ≤ ${charges.targetPct}%`}
            tone={charges.ratio > (charges.target ?? 0.35) ? 'danger' : 'success'}
            hint="Part des charges dans le CA des 12 derniers mois. La cible 35 % permet de garder 65 % pour le salaire, la marge et la provision taxes."
          />
        </div>
      </section>

      {/* ─── 4. Actions prioritaires ─────────────────────────────── */}
      {data.actions.length > 0 && (
        <section>
          <RowLabel icon={<AlertTriangle className="h-3 w-3 text-warning" />}>
            Actions prioritaires ({data.actions.length})
          </RowLabel>
          <Card>
            <CardContent className="p-3">
              <ul className="space-y-1.5">
                {data.actions.slice(0, 3).map((a, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm">
                    <Badge variant={a.severity === 'high' ? 'destructive' : a.severity === 'medium' ? 'warning' : 'secondary'}>
                      {a.severity}
                    </Badge>
                    <div className="min-w-0 flex-1">
                      <div className="truncate font-medium">{a.title}</div>
                      {(a.detail || a.meta) && (
                        <div className="truncate text-xs text-muted-foreground">{a.detail || a.meta}</div>
                      )}
                    </div>
                  </li>
                ))}
              </ul>
              {data.actions.length > 3 && (
                <p className="mt-2 text-xs text-muted-foreground">
                  +{data.actions.length - 3} autre(s) — voir l'onglet Actions
                </p>
              )}
            </CardContent>
          </Card>
        </section>
      )}

      {/* ─── Détail complet (replié : hors du coup d'œil) ────────── */}
      <details className="group rounded-lg border bg-card">
        <summary className="flex cursor-pointer list-none items-center gap-2 px-3 py-2 text-xs font-medium text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring">
          <ChevronDown className="h-3.5 w-3.5 transition-transform group-open:rotate-180" />
          Détail complet — composition du MRR, paliers d'épargne, charges &amp; derniers encaissements
        </summary>

        <div className="space-y-4 border-t px-3 py-3">
          {/* Paliers d'épargne */}
          {savings && (
            <div>
              <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold">
                <PiggyBank className="h-3.5 w-3.5" />
                Sécurité financière — paliers d'épargne
                <SectionHint>
                  Combien de mois d'activité tu peux couvrir si plus aucun revenu ne rentre, en piochant uniquement sur le compte épargne.
                  {' '}P1 = 3 mois de charges, P2 = 6 mois, P3 = 12 mois. Calcul basé sur ton burn rate.
                </SectionHint>
              </h3>
              <div className="grid grid-cols-3 gap-2">
                <TierMarker tier={1} label="3 mois" target={savings.tier1} current={savings.balance} />
                <TierMarker tier={2} label="6 mois" target={savings.tier2} current={savings.balance} />
                <TierMarker tier={3} label="12 mois" target={savings.tier3} current={savings.balance} />
              </div>
              {savings.nextTier !== null && (
                <div className="mt-2">
                  <div className="mb-1 flex items-center justify-between text-xs text-muted-foreground">
                    <span>Vers P{savings.nextTier}</span>
                    <span className="num">{Math.round(savings.progressToNext)}% · {fmtEuro(savings.balance)} / {fmtEuro(savings.nextTierTarget)}</span>
                  </div>
                  <Progress value={savings.progressToNext} indicatorClassName="bg-info" />
                </div>
              )}
            </div>
          )}

          {/* Composition du MRR + charges */}
          <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
            <KpiCard size="sm" label="MRR Sites" value={fmtEuro(mrr.sitesMRR)} sub={`${mrr.totalSites} sites`}
              hint="Part du MRR provenant uniquement des contrats de maintenance sites web (le parc AKAZ)." />
            <KpiCard size="sm" label="MRR récurrents" value={fmtEuro(mrr.recurringMRR)} sub={`${mrr.recurringCount} abos`}
              hint="Autres abonnements récurrents hors sites web (SAS Le Rocher, SAAS, services à valeur ajoutée…)." />
            <KpiCard size="sm" label="ARR projeté" value={fmtEuro(mrr.arr)} sub="Annuel" tone="info"
              hint="ARR = revenu récurrent annuel projeté = MRR × 12." />
            <KpiCard size="sm" label="Factures du mois" value={`${pipeline.invoicesThisMonth} / ${pipeline.invoicesTarget ?? '—'}`} sub="Objectif mensuel"
              hint="Nombre de factures émises ce mois-ci vs ton objectif mensuel." />
            <KpiCard size="sm" label="Charges 12 mois" value={fmtEuro(charges.totalLast12)} sub="Cumul"
              hint="Total des charges décaissées sur les 12 derniers mois (hors salaire et hors taxes URSSAF/IS/TVA)." />
            <KpiCard size="sm" label="Salaire moyen" value={fmtEuro(salary.avgLast12)} sub={`Cible ${fmtEuro(salary.target ?? 0)} net`} tone="info"
              hint="Moyenne mensuelle de tes virements salaire SASU sur les 12 derniers mois." />
          </div>

          {/* Abonnements Stripe : l'engagement en cours, compté dans le MRR. */}
          {(mrr.stripeMRR ?? 0) > 0 && (
            <div className="rounded-md border bg-muted/20 px-2 py-1.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-muted-foreground">
                  dont abonnements Stripe
                  <SectionHint>
                    Somme des abonnements Stripe ACTIFS ramenés au mois. C'est un engagement en cours, pas de la trésorerie : il ne bouge pas si aucun virement n'arrive ce mois-ci. Les paiements en 3× et les comptes internes en sont exclus.
                  </SectionHint>
                </span>
                <span className="num font-semibold text-success">
                  {fmtEuro(mrr.stripeMRR ?? 0)} · {mrr.stripeSubsCount ?? 0} abo(s)
                  {(mrr.stripeSubsExcluded ?? 0) > 0 && (
                    <span className="ml-1 font-normal text-muted-foreground">· {mrr.stripeSubsExcluded} écarté(s)</span>
                  )}
                </span>
              </div>
            </div>
          )}

          {/* Stripe encaissé : trésorerie, volontairement distincte du MRR. */}
          {(mrr.stripeEncaisseMRR ?? 0) > 0 && (
            <div className="rounded-md border bg-muted/20 px-2 py-1.5 text-xs">
              <div className="flex items-center justify-between">
                <span className="flex items-center gap-1 text-muted-foreground">
                  dont Stripe encaissé (30j glissants)
                  <SectionHint>
                    Virements Stripe récurrents réellement reçus sur Qonto ces 30 derniers jours (net de frais). C'est de la TRÉSORERIE : ce montant n'entre pas dans le MRR, qui se lit sur les abonnements actifs juste au-dessus. Un mois sans virement ne supprime pas l'abonnement.
                  </SectionHint>
                </span>
                <span className="num font-semibold">{fmtEuro(mrr.stripeEncaisseMRR ?? 0)} · {mrr.stripeCount ?? 0} client(s)</span>
              </div>
              {mrr.stripeClients && mrr.stripeClients.length > 0 && (
                <ul className="mt-1 space-y-0.5">
                  {mrr.stripeClients.map((c, i) => (
                    <li key={i} className="flex items-center justify-between gap-2">
                      <span className="truncate text-muted-foreground">
                        {c.clients.join(', ') || '—'}{c.services.length ? ` · ${c.services.join(' + ')}` : ''}
                      </span>
                      <span className="shrink-0 num">{fmtEuro(c.amount)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}

          {/* Derniers encaissements */}
          <div>
            <h3 className="mb-2 text-xs font-semibold">Derniers encaissements</h3>
            <div className="max-h-56 overflow-y-auto rounded-md border bg-muted/20 text-xs">
              <ul className="divide-y">
                {inflows.recent.map((tx, i) => (
                  <li key={i} className="flex items-center justify-between gap-2 px-2 py-1.5">
                    <div className="min-w-0 flex-1">
                      <div className="truncate">{tx.label}</div>
                      {tx.enrichment?.summary && (
                        <div className="truncate text-[11px] text-muted-foreground">{tx.enrichment.summary}</div>
                      )}
                    </div>
                    <span className="shrink-0 text-muted-foreground">{fmtDate(tx.date)}</span>
                    <span className="shrink-0 num font-semibold text-success">+{fmtEuro(tx.amount)}</span>
                  </li>
                ))}
                {inflows.recent.length === 0 && (
                  <li className="px-2 py-3 text-center text-muted-foreground">Aucune entrée ce mois-ci</li>
                )}
              </ul>
            </div>
          </div>
        </div>
      </details>

      <p className="text-right text-[10px] text-muted-foreground">
        Données générées le {new Date(data.generated_at).toLocaleString('fr-FR')}
        {data.fromCache && ' · cache'}
      </p>
    </div>
  );
}

function OverviewSkeleton() {
  return (
    <div className="space-y-3">
      {Array.from({ length: 3 }).map((_, row) => (
        <div key={row} className="grid grid-cols-2 gap-3 xl:grid-cols-4">
          {Array.from({ length: 4 }).map((_, i) => <Skeleton key={i} className={row === 0 ? 'h-28' : 'h-24'} />)}
        </div>
      ))}
      <Skeleton className="h-24" />
    </div>
  );
}

function ErrorPanel({ msg }: { msg: string }) {
  return (
    <Card>
      <CardContent className="flex items-center gap-3 py-6">
        <AlertTriangle className="h-5 w-5 text-destructive" />
        <div>
          <div className="font-semibold">Impossible de charger les données</div>
          <div className="text-sm text-muted-foreground">{msg}</div>
        </div>
      </CardContent>
    </Card>
  );
}
